import { useQuery } from '@tanstack/react-query';
import { ApiConfig } from '@/config/api.config';
import { ApiHookProps } from '@/@types/api.types';
import { AccommodationDetailsResponseDto } from '@/features/accommodations/types/accommodations-dto';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';
import { UrlHelper } from '@/utils/UrlHelper';
import { DateTimeHelper } from '@/utils/dateTimeHelper';

type Props = {
    accommodationId: string;
    apartmentId: string;
    startDate?: Date;
    endDate?: Date;
} & ApiHookProps;

export const useAccommodationsApi = ({ accommodationId, apartmentId, startDate, endDate }: Props) => {
    const axiosInstance = useAxiosAuth();

    const {
        data: accommodationDetailsData,
        isLoading: isLoadingAccommodationDetailsData,
        isError: isErrorAccommodationDetailsData,
    } = useQuery({
        queryKey: [
            ApiConfig.queries.features.accommodations.queryKeys.details,
            accommodationId,
            apartmentId,
            startDate,
            endDate,
        ],
        queryFn: async (): Promise<AccommodationDetailsResponseDto> => {
            const path = ApiConfig.queries.features.accommodations.endpoints.get
                .replace('{accommodationId}', accommodationId)
                .replace('{apartmentId}', apartmentId);
            const url = UrlHelper.buildUrlWithQueryParams(path, {
                startDate: startDate ? DateTimeHelper.getDateOnly(startDate) : undefined,
                endDate: endDate ? DateTimeHelper.getDateOnly(endDate) : undefined,
            });
            const { data } = await axiosInstance.get<AccommodationDetailsResponseDto>(url);
            return data;
        },
        enabled: !!accommodationId && !!apartmentId,
    });

    return {
        accommodationDetailsData,
        isLoadingAccommodationDetailsData,
        isErrorAccommodationDetailsData,
    };
};
